<template>
  <div class="product-buy" itemprop="offers" itemscope itemtype="https://schema.org/Offer">
    <div class="product-buy__details">
      <div class="product-buy__price-and-from">
        <p class="product-buy__price" itemprop="price">
          <span itemprop="price">{{ formatPrice(product.price, product.price_unit_text, false) }} </span>
          <span itemprop="priceCurrency">{{ $t(`units.currencies.short.${product.price_unit_text}`) }}</span> /
          <span class="product-buy__unit">{{ $t(`units.weight.${product.wt_unit.name}`) }}</span>
          <span v-if="product.discount > 0" class="product-buy__price-prev">
            <span>
              {{ formatPrice(product.full_price, product.price_unit_text) }}
            </span>
            <span> (-{{ product.discount }}%) </span>
          </span>

          <link
            itemprop="availability"
            :href="
              isExpired
                ? 'http://schema.org/OutOfStock'
                : product.seller.is_system_seller
                ? 'http://schema.org/InStock'
                : 'http://schema.org/PreOrder'
            "
          />
        </p>

        <p class="product-buy__from">
          {{ $t('from {0} {1}', [product.weight, $t(`units.weight.${product.wt_unit.name}`)]) }}
        </p>
      </div>

      <div class="product-buy__quantity">
        <Counter
          :value="cartInputValue"
          :readonly="isInCart || !canAddToCart"
          color="gray"
          :step="1"
          :min="product.weight"
          class="product-buy__field"
          @input="val => $store.dispatch('productPage/setCartInputValue', val)"
        />
        <span>{{ $t(`units.weight.${product.wt_unit.name}`) }}</span>
      </div>
    </div>

    <div class="product-buy__actions">
      <button
        v-if="canAddToCart"
        class="button-reset product-buy__action product-buy__action--cart"
        type="button"
        :disabled="isAddingToCart"
        @click="handleCartButtonClick"
      >
        <template v-if="isInCart">
          <span class="product-buy__action-button-text">{{ $t('In cart') }}</span>
          <Check />
        </template>

        <template v-else>
          <span class="product-buy__action-button-text">{{ $t('Add to cart') }}</span>
          <b-spinner v-if="isAddingToCart" class="product-buy__spinner" show small variant="light" />
          <i v-else class="icon-cart"></i>
        </template>
      </button>

      <div v-else class="product-buy__action product-buy__action--out-of-stock">
        <span class="product-buy__action-button-text">{{ $t('Not available') }}</span>
        <Ban />
      </div>

      <!-- <button class="button-reset product-buy__action product-buy__action--fav" type="button">
              <Heart :size="19" />
            </button> -->
    </div>

    <p
      v-if="!isExpired && !isOutOfStock"
      :class="{
        'product-buy__availability': true,
        'product-buy__availability--available': product.seller.is_system_seller,
      }"
    >
      {{ product.seller.is_system_seller ? $t('marketProduct.inStock') : $t('marketProduct.preorder') }}
    </p>
  </div>
</template>

<script>
import formatPrice from '@/helpers/filters/formatPrice'
import Counter from '@/components/form/Counter.vue'
import { mapGetters, mapState } from 'vuex'
import { isExpiredProduct } from '@/helpers/getters/isExpiredProduct'
import { eventBus } from '@/helpers'
import { ECOMMERCE_ACTION_TYPE, Metrics } from '@/utils/metrics'
import { Check, Ban } from 'lucide-vue'
import { SELLER_PRODUCT_STATUS } from '@/constants'
import { RR } from '@/plugins/retailRocket'

export default {
  name: 'ProductPageBuy',
  components: {
    Counter,
    Check,
    Ban,
  },
  props: {
    product: { type: Object, required: true },
  },
  data: () => ({
    isAddingToCart: false,
  }),
  computed: {
    ...mapState('productPage', ['cartInputValue']),
    ...mapGetters('cart', ['cartItemsProducts']),
    ...mapGetters('localCart', ['localCartItemsProducts']),
    ...mapGetters('user', ['isBuyer']),
    ...mapGetters('auth', ['loggedIn']),
    isInCart() {
      if (this.loggedIn) {
        return this.cartItemsProducts.some(item => item && item.seller_product.id === this.product.id)
      }

      return this.localCartItemsProducts.some(p => p.id === this.product.id)
    },
    isExpired() {
      return isExpiredProduct(this.product)
    },
    isOutOfStock() {
      return this.product.status === SELLER_PRODUCT_STATUS.not_publish
    },
    canAddToCart() {
      return (this.isBuyer || !this.loggedIn) && !this.isExpired && !this.isOutOfStock
    },
  },
  created() {
    this.$store.dispatch('productPage/setCartInputValue', this.product.weight)
  },
  methods: {
    formatPrice,
    async handleCartButtonClick() {
      this.isAddingToCart = true

      try {
        if (this.isInCart) {
          if (this.loggedIn) {
            this.$router.push({ name: 'cart' })
          } else {
            eventBus.$emit('showCartSidebar', true)
          }

          return
        }

        if (this.loggedIn) {
          await this.$store.dispatch('cart/addToCart', {
            seller_product: this.product.id,
            weight: this.cartInputValue,
          })

          this.$notif({ success: true, title: this.$t('Product added to cart') })

          Metrics.pushEcommerce({
            actionType: ECOMMERCE_ACTION_TYPE.add,
            products: [
              {
                ...this.product,
                quantity: this.cartInputValue,
              },
            ],
          })
          RR.reportAddToCart(this.product.id)
        } else {
          this.$store.dispatch('localCart/addToLocalCart', { ...this.product, weight: this.cartInputValue })

          Metrics.pushEcommerce({
            actionType: ECOMMERCE_ACTION_TYPE.add,
            products: [
              {
                ...this.product,
                quantity: this.cartInputValue,
              },
            ],
          })
          RR.reportAddToCart(this.product.id)
          eventBus.$emit('showCartSidebar', true)
        }
      } catch (e) {
        console.error(e)
        eventBus.$emit('error', e)
      } finally {
        this.isAddingToCart = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/common-imports.scss';

.product-buy {
  padding: 18px;
  background-color: $White;
  border-radius: 4px;
  box-shadow: 3px 6px 12px rgba(0, 0, 0, 0.2);
  display: grid;
  gap: 8px;

  @include media-breakpoint-up(xl) {
    padding: 32px 30px;
    gap: 12px;
    border-radius: 8px;
    box-shadow: 5px 10px 20px rgba(0, 0, 0, 0.2);
  }

  &--pinned {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1005;
    width: 100%;
    grid-template-columns: 1fr auto;
    align-items: center;
    border-top-left-radius: 0;
    border-top-right-radius: 0;

    .product-buy__availability {
      display: none;
    }

    .product-buy__action-button-text {
      display: none;
    }

    .product-buy__action--out-of-stock svg {
      display: block;
    }
  }

  &__price-and-from {
    margin-bottom: 12px;

    @include media-breakpoint-up(xl) {
      margin-bottom: 24px;
    }
  }

  &__price {
    font-weight: 700;
    font-size: 18px;
    line-height: 20px;

    @include media-breakpoint-up(xl) {
      font-size: 30px;
      line-height: 32px;
    }
  }

  &__unit {
    margin-right: 8px;
  }

  &__price-prev {
    font-weight: 400;
    font-size: 14px;
    line-height: 1.15;
    color: $DGray;
    white-space: nowrap;

    > span:first-child {
      text-decoration: line-through;
    }

    > span:last-child {
      padding-left: 4px;
      color: $PlainRed;
    }

    @include media-breakpoint-up(xl) {
      font-size: 18px;
      line-height: 20px;
    }
  }

  &__from {
    font-size: 14px;
    line-height: 1.15;
    font-weight: 500;
    color: $DGray;
    margin-top: 2px;

    @include media-breakpoint-up(xl) {
      font-size: 18px;
      line-height: 20px;
    }
  }

  &__quantity {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 12px;
    line-height: 14px;
    font-weight: 600;

    @include media-breakpoint-up(xl) {
      gap: 20px;
      font-size: 18px;
      line-height: 20px;
    }
  }

  &__field {
    flex: 1;

    &::v-deep {
      .input {
        padding: 5px 10px;
        min-height: 24px;

        @include media-breakpoint-up(xl) {
          padding: 8px 12px;
          min-height: 36px;
        }
      }

      .input__field {
        padding: 0;
        font-size: 14px;
        line-height: 1.15;
        font-weight: 500;
        color: $DGray;

        @include media-breakpoint-up(xl) {
          font-size: 18px;
          line-height: 20px;
        }
      }

      .counter__button:not(:last-child) {
        @include media-breakpoint-up(xl) {
          margin-right: 12px;
        }
      }

      .counter__button svg {
        @include media-breakpoint-up(xl) {
          width: 18px;
          height: 18px;
        }
      }
    }
  }

  &__actions {
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
  }

  &__action {
    height: 42px;
    min-width: 42px;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    @include media-breakpoint-up(xl) {
      height: 68px;
      min-width: 68px;
    }

    &:disabled {
      opacity: 0.7;
    }

    > svg {
      width: 18px;
      height: 18px;

      @include media-breakpoint-up(xl) {
        width: 24px;
        height: 24px;
      }
    }

    > i {
      font-size: 18px;

      @include media-breakpoint-up(xl) {
        font-size: 30px;
      }
    }

    &--cart {
      background-color: $GreenM;
      color: $White;
      transition: background-color 200ms ease-in-out;

      &:not(:disabled):hover {
        background-color: darken(#1d9f3a, 5);
      }
    }

    &--out-of-stock {
      border: 1px solid $LGray6;
      color: $Gray2;
      cursor: default;

      > .product-buy__action-button-text {
        margin-right: 0;
      }

      > svg {
        display: none;
      }
    }

    &--fav {
      width: 42px;
      background-color: $LGray3;
      color: $GreenM;

      @include media-breakpoint-up(xl) {
        width: 68px;
      }
    }
  }

  &__action-button-text {
    font-weight: 600;
    font-size: 16px;
    line-height: 18px;
    margin-right: 8px;

    @include media-breakpoint-up(xl) {
      margin-right: 14px;
      font-size: 20px;
      line-height: 22px;
    }
  }

  &__spinner {
    width: 18px;
    height: 18px;
    margin: 0;

    @include media-breakpoint-up(xl) {
      width: 24px;
      height: 24px;
    }
  }

  &__availability {
    text-align: center;
    font-weight: 500;
    font-size: 14px;
    line-height: 16px;
    color: $DGray;
    margin-top: -4px;

    @include media-breakpoint-up(xl) {
      font-size: 18px;
      line-height: 20px;
    }

    &--available {
      color: $GreenM;
    }
  }
}
</style>
